import type { FormikProps } from 'formik';
import { useCallback } from 'react';

import './VendorLeadBillingInfoContent.scss';

import { SelectField, TextInputField } from 'src/components/base';
import { TagListSelectField } from 'src/components/base/fields/TagListSelectField';
import type { FormVendorProductLeadBillingInfoValues } from 'src/components/vendors/billing/VendorLeadBillingInfo.validation';
import { CANADA_STATE_OPTIONS, COUNTRY_OPTIONS, Country, USA_STATE_OPTIONS } from 'src/types';
import { PaymentMethod, paymentMethodsOptions } from 'src/types/vendors/PaymentMethod';
import { vendorPaymentTermsOptions } from 'src/types/vendors/VendorPaymentTerms';

type VendorLeadBillingInfoContentProps = {
  formikProps: FormikProps<FormVendorProductLeadBillingInfoValues>;
};

export function VendorLeadBillingInfoContent({
  formikProps: { values, setValues },
}: Readonly<VendorLeadBillingInfoContentProps>) {
  const handleCountryChange = useCallback(
    (newCountry: string) => {
      void setValues(
        (prev) => ({
          ...prev,

          billingAddress: {
            country: newCountry,

            region: null,
            city: null,
            street: null,
            postalCode: null,
          },
        }),
        false
      );
    },
    [setValues]
  );

  const handlePaymentMethodChange = useCallback(() => {
    void setValues(
      (prev) => ({
        ...prev,

        bankAccountNumber: null,
        routingNumber: null,
      }),
      false
    );
  }, [setValues]);

  const country = values.billingAddress.country;
  const isBankAccountFieldsVisible = values.paymentMethod === PaymentMethod.Ach;
  const isRegionDisabled = !country;

  return (
    <div className="vendor-lead-billing-info-content__container">
      <div className="vendor-lead-billing-info-content__container__block">
        <h2>Payment Info</h2>
        <div className="vendor-lead-billing-info-content__container__row">
          <div className="w-100">
            <TagListSelectField
              controlClassName="vendor-lead-billing-info-content__container__tag-options-control"
              optionsClassName="vendor-lead-billing-info-content__container__tag-options"
              optionClass="vendor-lead-billing-info-content__container__tag-option"
              name="paymentTerms"
              label="Payment Terms"
              options={vendorPaymentTermsOptions}
              touchOnMount={true}
            />
          </div>
        </div>

        <div className="vendor-lead-billing-info-content__container__row">
          <div className="w-100 vendor-lead-billing-info-content__container__row">
            <TagListSelectField
              controlClassName="vendor-lead-billing-info-content__container__tag-options-control"
              optionsClassName="vendor-lead-billing-info-content__container__tag-options"
              optionClass="vendor-lead-billing-info-content__container__tag-option"
              name="paymentMethod"
              label="Payment Method"
              options={paymentMethodsOptions}
              onChange={handlePaymentMethodChange}
              touchOnMount={true}
            />
            <div className="w-100">
              {isBankAccountFieldsVisible ? (
                <TextInputField name="bankAccountNumber" label="Bank Account Number" touchOnMount={true} />
              ) : (
                ' '
              )}
            </div>
          </div>
          <div className="w-100 vendor-lead-billing-info-content__container__row">
            {isBankAccountFieldsVisible ? (
              <TextInputField name="bankAccountNumberConfirm" label="Bank Account Number Confirm" touchOnMount={true} />
            ) : (
              ' '
            )}
            {isBankAccountFieldsVisible ? (
              <TextInputField name="routingNumber" label="Routing Number" touchOnMount={true} />
            ) : (
              ' '
            )}
          </div>
        </div>
      </div>

      <div className="vendor-lead-billing-info-content__container__block">
        <h2>Payment Contact</h2>
        <div className="vendor-lead-billing-info-content__container__row">
          <div className="w-100">
            <TextInputField name="paymentContact.firstName" label="First Name" touchOnMount={true} />
          </div>
          <div className="w-100">
            <TextInputField name="paymentContact.lastName" label="Last Name" touchOnMount={true} />
          </div>
        </div>
        <div className="vendor-lead-billing-info-content__container__row">
          <div className="w-100">
            <TextInputField name="paymentContact.emailAddress" label="Email Address" touchOnMount={true} />
          </div>
          <div className="w-100">
            <TextInputField type="phone" name="paymentContact.phoneNumber" label="Phone Number" touchOnMount={true} />
          </div>
        </div>
      </div>

      <div className="vendor-lead-billing-info-content__container__block">
        <h2>Billing Address</h2>
        <div className="vendor-lead-billing-info-content__container__row">
          <div className="w-100">
            <SelectField
              name="billingAddress.country"
              label="Country"
              placeholder="Select Country"
              options={COUNTRY_OPTIONS}
              onChange={handleCountryChange}
              touchOnMount={true}
            />
          </div>
          <div className="w-100">
            <SelectField
              name="billingAddress.region"
              label={country === Country.Canada ? 'Province/Territory' : 'State'}
              placeholder={country === Country.Canada ? 'Select Province/Territory' : 'Select State'}
              options={country === Country.Canada ? CANADA_STATE_OPTIONS : USA_STATE_OPTIONS}
              disabled={isRegionDisabled}
              touchOnMount={true}
            />
          </div>
        </div>
        <div className="vendor-lead-billing-info-content__container__row">
          <div className="w-100">
            <TextInputField name="billingAddress.street" label="Street" touchOnMount={true} />
          </div>
          <div className="vendor-lead-billing-info-content__container__subrow">
            <div className="w-100">
              <TextInputField name="billingAddress.city" label="City" touchOnMount={true} />
            </div>
            <TextInputField
              name="billingAddress.postalCode"
              type="zip"
              country={country ?? undefined}
              label={country === Country.Canada ? 'Postal Code' : 'Zip'}
              disabled={isRegionDisabled}
              touchOnMount={true}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
